import { useMemo } from "react";
import { CommonFacetView } from "./CommonFacetView";
import type { DashboardContext, FacetViewProps } from "./dashboard-kinds";
import { ProjectDocuments } from "./ProjectDocuments";
import {
  parseCandidates,
  parseDocuments,
  parseEvents,
  parsePackages,
  parseRequirements,
  type PlanPerson,
} from "./project-plan";
import { ProjectCandidatesScreen, type CandidatePath } from "./screens/ProjectCandidatesScreen";
import { ProjectGuidedAufteilenScreen } from "./screens/ProjectGuidedAufteilenScreen";
import { ProjectTeamScreen } from "./screens/ProjectTeamScreen";
import { ProjectToMatchScreen } from "./screens/ProjectToMatchScreen";
import { useLinkedMatches } from "./use-linked-matches";
import { useProjectPeople } from "./use-project-people";

// Die Flächen eines Projekts der Art PROJECT. Jede ist die Arbeitsfläche selbst, nicht ein Link auf
// einen eigenen Screen; ihr Stand liegt im data-Dokument des Dashboards.
export function ProjectFacetView({ facet, ctx, view, onViewChange }: FacetViewProps) {
  switch (facet) {
    case "aufteilen":
      return <PlanFacet ctx={ctx} />;
    case "dokumente":
      return (
        <ProjectDocuments
          documents={parseDocuments(ctx.data.documents)}
          onChange={(next) => {
            ctx.editData({ documents: next });
            ctx.keepFacets(["dokumente"]);
          }}
          readOnly={ctx.readOnly}
        />
      );
    case "mitglieder":
      return <TeamFacet ctx={ctx} />;
    case "kandidaten":
      return <MatchFacet ctx={ctx} path={asCandidatePath(view)} onPathChange={onViewChange} />;
    default:
      return <CommonFacetView facet={facet} ctx={ctx} />;
  }
}

function asCandidatePath(view: string | null): CandidatePath {
  return view === "known" || view === "existingMatch" || view === "match" ? view : null;
}

/**
 * Wer zuweisbar ist und wie die Zugewiesenen heißen: die Eigentümerin, die Menschen mit Zugriff und
 * die Gefragten. Hängt am Plan, weil die Spuren des Personfokus den tatsächlich Beteiligten folgen.
 */
function usePeople(ctx: DashboardContext, candidates: readonly string[]) {
  const { members } = ctx.entity;
  const createdBy = ctx.entity.company ? null : ctx.entity.createdBy;
  const packages = useMemo(() => parsePackages(ctx.data.packages), [ctx.data.packages]);
  const all = useMemo<readonly PlanPerson[]>(
    () => (createdBy ? [createdBy, ...members] : members),
    [createdBy, members],
  );
  return { packages, people: useProjectPeople(packages, all, candidates) };
}

function useCandidates(ctx: DashboardContext) {
  const candidates = useMemo(() => parseCandidates(ctx.data.candidates), [ctx.data.candidates]);
  /** Gefragte Menschen. Wer jemanden ins Projekt holt, hat ein Team: die Fläche dafür bleibt dann. */
  function edit(next: readonly string[]) {
    ctx.editData({ candidates: next });
    if (next.length > 0) ctx.keepFacets(["mitglieder"]);
  }
  return [candidates, edit] as const;
}

function PlanFacet({ ctx }: { ctx: DashboardContext }) {
  const [candidates] = useCandidates(ctx);
  const { packages, people } = usePeople(ctx, candidates);
  const events = useMemo(() => parseEvents(ctx.data.events), [ctx.data.events]);
  return (
    <ProjectGuidedAufteilenScreen
      packages={packages}
      events={events}
      people={people.assignable}
      involved={people.involved}
      lookupPerson={people.lookup}
      // Der Plan ändert sich: damit ist die Fläche befüllt und gehört ab hier zum Projekt.
      onChange={(next) => {
        ctx.editData({ packages: next });
        ctx.keepFacets(["aufteilen"]);
      }}
      onEventsChange={(next) => {
        ctx.editData({ events: next });
        ctx.keepFacets(["aufteilen"]);
      }}
      readOnly={ctx.readOnly}
    />
  );
}

function TeamFacet({ ctx }: { ctx: DashboardContext }) {
  const [candidates, editCandidates] = useCandidates(ctx);
  return (
    <ProjectTeamScreen
      owner={ctx.entity.company ? null : ctx.entity.createdBy}
      members={ctx.entity.members}
      candidates={candidates}
      onRemoveCandidate={(id) => editCandidates(candidates.filter((c) => c !== id))}
      onGoToMatch={(path) => ctx.openFacet("kandidaten", path)}
      readOnly={ctx.readOnly}
    />
  );
}

function MatchFacet({
  ctx,
  path,
  onPathChange,
}: {
  ctx: DashboardContext;
  path: CandidatePath;
  onPathChange: (next: CandidatePath) => void;
}) {
  const [candidates, editCandidates] = useCandidates(ctx);
  const { people } = usePeople(ctx, candidates);
  // Die Matches, die aus diesem Projekt entstanden sind. Die Relation lebt in genesis
  // (project_match), nicht im Dokument; die vollen Daten für die Kachel holt der Hook nach.
  const linkedMatches = useLinkedMatches(ctx.entity.matchIds);
  const requirements = useMemo(() => parseRequirements(ctx.data.requirements), [ctx.data.requirements]);
  const unlinkMatch = ctx.source.unlinkMatch;
  return (
    <ProjectCandidatesScreen
      candidates={candidates}
      onCandidatesChange={editCandidates}
      linkedMatches={linkedMatches}
      path={path}
      onPathChange={onPathChange}
      onUnlinkMatch={unlinkMatch && !ctx.readOnly ? unlinkMatch : undefined}
      readOnly={ctx.readOnly}
      texts={ctx.config.matchFacet}
      matchWizard={
        <ProjectToMatchScreen
          toMatch={ctx.config.toMatch}
          link={ctx.source.linkMatch ?? null}
          actingAsCompanyId={ctx.source.actingAsCompanyId}
          title={ctx.title}
          description={ctx.description}
          requirements={requirements}
          people={people.assignable}
          documents={parseDocuments(ctx.data.documents)}
        />
      }
    />
  );
}
